import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import Stepper from './Stepper';
import StepAiSetup from './StepAiSetup';
import StepConfigure from './StepConfigure';
import StepQuestions from './StepQuestions';
import StepPublish from './StepPublish';
import Alert from '../admincomponents/components/Alert';
import Loading from '../admincomponents/components/Loading';
import {
  aiGenerateQuiz,
  aiCreateQuestionCreateGame,
  fetchQuizPreview,
} from '../../apiservices/axiosservice/subscriberservice';
import { getSubscriber } from '../../apiservices/LocalStorageService';
import './SmartQuiz.css';

const steps = [
  { label: 'AI Setup' },
  { label: 'Configure' },
  { label: 'Questions' },
  { label: 'Publish' },
];

export interface AiSetupData {
  topic: string;
  questionCount: number;
  difficulty: string;
}

export interface ConfigureData {
  gameTitle: string;
  maxPlayers: number;
  timePerQuestion: number;
  randomiseOrder: boolean;
  allowLateJoiners: boolean;
  interactiveMode: boolean;
}

const SmartQuizSetup: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');

  // Step 1 data
  const [setupData, setSetupData] = useState<AiSetupData>({
    topic: '',
    questionCount: 10,
    difficulty: 'Medium',
  });

  // Step 2 data
  const [configData, setConfigData] = useState<ConfigureData>({
    gameTitle: '',
    maxPlayers: 0, // 0 = unlimited
    timePerQuestion: 30,
    randomiseOrder: true,
    allowLateJoiners: false,
    interactiveMode: true,
  });

  // Step 3 data
  const [questions, setQuestions] = useState<CreateQuestionItem[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [gameId, setGameId] = useState('');
  const [isLastPage, setIsLastPage] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  // Step 4 data
  const [publishedGame, setPublishedGame] = useState<any>(null);

  const showAlert = (msg: string) => setAlertMsg(msg);

  // Step 1 → 2: Generate questions via AI
  const handleGenerateQuiz = useCallback(async () => {
    if (!setupData.topic.trim()) {
      showAlert('Please enter a quiz topic');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        quizCount: setupData.questionCount,
        quizDescription: setupData.topic,
        optionTypes: ['single', 'multiple', 'trueorfalse'],
      };
      const res = await aiGenerateQuiz(payload);
      const { gameId: gId, pageResult } = res.data;
      setGameId(gId);
      setQuestions(pageResult.content || []);
      setIsLastPage(pageResult.last);
      setCurrentPage(pageResult.pageNumber);
      setSelectedIdx(0);
      setCurrentStep(2);
    } catch (err: any) {
      showAlert(err.message || 'Failed to generate quiz');
    } finally {
      setLoading(false);
    }
  }, [setupData]);

  // Load more questions (next page)
  const handleLoadMore = useCallback(async () => {
    if (isLastPage || loadingMore || !gameId) return;
    setLoadingMore(true);
    try {
      const res = await fetchQuizPreview(gameId, currentPage + 1);
      const { pageResult } = res.data;
      setQuestions((prev) => [...prev, ...(pageResult.content || [])]);
      setIsLastPage(pageResult.last);
      setCurrentPage(pageResult.pageNumber);
    } catch (err: any) {
      showAlert(err.message || 'Failed to load more questions');
    } finally {
      setLoadingMore(false);
    }
  }, [gameId, currentPage, isLastPage, loadingMore]);

  // Step 2 → 3
  const handleGoToQuestions = () => {
    if (!configData.gameTitle.trim()) {
      showAlert('Please enter a game title');
      return;
    }
    setCurrentStep(3);
  };

  // Step 3 → 4: Publish / Create game
  const handlePublish = useCallback(async () => {
    const subscriber = getSubscriber();
    if (!subscriber?.userId) {
      showAlert('User session not found');
      return;
    }
    setLoading(true);
    try {
      const req: CreateQuizGameRequest = {
        gameId,
        source: 'AI',
        gameTitle: configData.gameTitle,
        timerEnabled: configData.timePerQuestion > 0,
        gameOwnerId: subscriber.userId,
        gameType: 'quiz',
        gameCapacity: configData.maxPlayers || 999,
        scheduledGame: !configData.interactiveMode, // If not interactive, treat as scheduled (players join before start)
      };
      const res = await aiCreateQuestionCreateGame(req);
      setPublishedGame(res.data || { gameId: res.gameId });
      setCurrentStep(4);
    } catch (err: any) {
      showAlert(err.message || 'Failed to create game');
    } finally {
      setLoading(false);
    }
  }, [gameId, configData]);

  const handleSaveDraft = () => {
    showAlert('Draft saved (feature coming soon)');
  };

  const handleBack = () => {
    if (currentStep === 1) {
      navigate('/dashboard/games');
    } else {
      setCurrentStep((s) => s - 1);
    }
  };

  return (
    <div className="sq-page">
      {/* Header */}
      <div className="sq-page__header">
        <button className="sq-page__back" onClick={handleBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h1 className="sq-page__title">Smart Quiz Setup</h1>
      </div>

      {/* Breadcrumb */}
      <div className="sq-page__breadcrumb">
        <span className="sq-page__crumb">Game Type</span>
        <span className="sq-page__crumb-sep">/</span>
        <span className="sq-page__crumb sq-page__crumb--active">
          Smart Quiz
        </span>
      </div>

      {/* Stepper */}
      <Stepper steps={steps} currentStep={currentStep} />

      {/* Step Content */}
      <div className="sq-page__content">
        {currentStep === 1 && (
          <StepAiSetup
            data={setupData}
            onChange={setSetupData}
            onGenerate={handleGenerateQuiz}
          />
        )}
        {currentStep === 2 && (
          <StepConfigure
            data={configData}
            onChange={setConfigData}
            onBack={() => setCurrentStep(1)}
            onNext={handleGoToQuestions}
          />
        )}
        {currentStep === 3 && (
          <StepQuestions
            questions={questions}
            selectedIdx={selectedIdx}
            onSelectIdx={setSelectedIdx}
            onSaveDraft={handleSaveDraft}
            onPublish={handlePublish}
            onLoadMore={handleLoadMore}
            isLastPage={isLastPage}
            loadingMore={loadingMore}
          />
        )}
        {currentStep === 4 && (
          <StepPublish
            game={publishedGame}
            gameTitle={configData.gameTitle}
            questionCount={questions.length}
            difficulty={setupData.difficulty}
            timePerQuestion={configData.timePerQuestion}
          />
        )}
      </div>

      {loading && <Loading message="Processing..." />}
      {alertMsg && (
        <Alert
          title="Notice"
          isOpen={!!alertMsg}
          cancelText="Ok"
          message={alertMsg}
          onCancel={() => setAlertMsg('')}
        />
      )}
    </div>
  );
};

export default SmartQuizSetup;
